import React, { useState, useEffect } from 'react';
import { X, FileText, Plus, Loader2, Image, Trash2, Upload, Edit3, Filter, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import { DEFAULT_SERVICES } from '../../utils/constants';
import { useLang } from '../../i18n';

const AdminGallery = () => {
  const { t } = useLang();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('');
  const [formData, setFormData] = useState({
    service_type: 'electrical',
    title: '',
    description: '',
    image_url: '',
    property_type: 'residential',
    file: null,
    preview: null
  });

  useEffect(() => {
    loadGallery();
  }, [filter]);

  const loadGallery = async () => {
    try {
      const url = filter ? `/api/gallery?service_type=${filter}` : '/api/gallery';
      const data = await api(url);
      setImages(data);
    } catch (err) {
      toast.error('Dështoi ngarkimi i galerisë');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ service_type: 'electrical', title: '', description: '', image_url: '', property_type: 'residential', file: null, preview: null });
    setEditItem(null);
  };

  const openEdit = (img) => {
    setEditItem(img);
    setFormData({ service_type: img.service_type, title: img.title, description: img.description || '', image_url: img.image_url, property_type: img.property_type || 'residential', file: null, preview: img.image_url });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let imageUrl = formData.image_url;
      if (formData.file) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', formData.file);
        const token = localStorage.getItem('token');
        const uploadRes = await fetch(`${API_URL}/api/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: uploadFormData });
        if (!uploadRes.ok) throw new Error('Ngarkimi dështoi');
        const uploadData = await uploadRes.json();
        imageUrl = uploadData.url;
      }

      if (editItem) {
        await api(`/api/gallery/${editItem.id}`, {
          method: 'PUT',
          body: JSON.stringify({ service_type: formData.service_type, title: formData.title, description: formData.description, image_url: imageUrl, property_type: formData.property_type })
        });
        toast.success('Foto u përditësua!');
      } else {
        await api('/api/gallery', {
          method: 'POST',
          body: JSON.stringify({ service_type: formData.service_type, title: formData.title, description: formData.description, image_url: imageUrl, property_type: formData.property_type })
        });
        toast.success('Foto u shtua në galeri!');
      }
      setShowForm(false);
      resetForm();
      loadGallery();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('A jeni i sigurt që doni ta fshini këtë foto?')) return;
    try {
      await api(`/api/gallery/${id}`, { method: 'DELETE' });
      toast.success('Foto u fshi');
      loadGallery();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="admin-gallery" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">{t('gal_page_title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{images.length} {t('gal_page_title').toLowerCase().includes('galer') ? 'foto' : 'photos'}</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary" data-testid="add-gallery-btn">
          <Plus className="w-4 h-4 inline mr-2" />
          {t('gal_upload_btn')}
        </button>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('')}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${!filter ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
          data-testid="gallery-filter-all"
        >
          {t('gal_all')}
        </button>
        {Object.entries(DEFAULT_SERVICES).map(([key, s]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${filter === key ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            data-testid={`gallery-filter-${key}`}
          >
            {s.name}
          </button>
        ))}
      </div>

      {/* Images Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((img) => (
          <div key={img.id} className="card overflow-hidden bg-gray-900 border-gray-800" data-testid={`admin-gallery-item-${img.id}`}>
            <img src={img.image_url} alt={img.title} className="w-full h-48 object-cover" />
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-white">{img.title}</h3>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => openEdit(img)}
                    className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded"
                    data-testid={`edit-gallery-${img.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteImage(img.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded"
                    data-testid={`delete-gallery-${img.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {img.description && <p className="text-sm text-gray-400 mb-2">{img.description}</p>}
              <span className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded capitalize">
                {img.service_type.replace('_', ' ')}
              </span>
            </div>
          </div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="card p-12 text-center text-gray-400">
          <Image className="w-12 h-12 mx-auto mb-3 text-gray-600" />
          <p>{t('gal_no_photos')}</p>
        </div>
      )}

      {/* Add/Edit Photo Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-md w-full" data-testid="add-gallery-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-white">{editItem ? t('edit') : t('gal_modal_upload')}</h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('gal_category')}</label>
                <select
                  className="input w-full"
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  required
                  data-testid="gallery-service-select"
                >
                  {Object.entries(DEFAULT_SERVICES).map(([key, s]) => (
                    <option key={key} value={key}>{s.name}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                placeholder={t('gal_title_field')}
                className="input w-full"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                data-testid="gallery-title-input"
              />
              <input
                type="text"
                placeholder={t('gal_description')}
                className="input w-full"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="gallery-description-input"
              />
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-red-500 transition-colors cursor-pointer relative">
                <input
                  type="file"
                  accept="image/*,.heic,.heif,.pdf,.doc,.docx"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const isImage = file.type.startsWith('image/');
                      setFormData({ ...formData, file, preview: isImage ? URL.createObjectURL(file) : null, image_url: 'uploading' });
                    }
                  }}
                  data-testid="gallery-file-input"
                />
                {formData.preview ? (
                  <img src={formData.preview} alt="Preview" className="w-full h-40 object-cover rounded" />
                ) : formData.file ? (
                  <div>
                    <FileText className="w-10 h-10 mx-auto mb-2 text-red-500" />
                    <p className="text-sm text-white">{formData.file.name}</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 mx-auto mb-2 text-gray-500" />
                    <p className="text-sm text-gray-400">{t('gal_select_file')}</p>
                    <p className="text-xs text-gray-600 mt-1">JPG, PNG, PDF, HEIC, WebP (max 25MB)</p>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
                data-testid="submit-gallery-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
                {editItem ? t('tech_save_changes') : t('gal_save')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== LIVE MAP (Pure Leaflet) =====================


export default AdminGallery;
